import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { tap, map, Subject, EMPTY } from 'rxjs';
import { switchMap, filter, debounceTime, distinctUntilChanged, takeUntil, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { Product} from 'src/app/shared/product';
import { ProductApiService } from 'src/app/services/product-api.service';

@Component({
  selector: 'app-productselector',
  templateUrl: './productselector.component.html',
  styleUrls: ['./productselector.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Reusable product type-ahead selector backed by `ProductApiService`.
 *
 * Two co-existing data flows:
 * 1. **Pre-selection flow** — a `searchText` `@Input()` change triggers `getProductByCode()`,
 *    which resolves an existing product code into the `product` model. Each new change cancels
 *    any in-flight request via `_preSelectionCancel$`.
 * 2. **Live-search flow** — user keystrokes drive `doSearch()` → `searchStr$` Subject →
 *    debounced `switchMap(getProductFastSearch())` → `products` suggestions list.
 *
 * API flag notes:
 * - `allowOld` is currently hard-disabled via `(this.allowOld && false)` — see inline comment.
 * - `allowComplex` passes through via `(this.allowComplex && true)` — the `&& true` is a no-op.
 *
 * Subscription management: `destroy$` tears down all long-lived subscriptions on destroy;
 * `_preSelectionCancel$` cancels the current in-flight pre-selection request before issuing a new one.
 */
export class ProductselectorComponent implements OnInit, OnChanges, OnDestroy {
  products: Product[];
  product: Product;

  /** Label text displayed above the autocomplete (currently unused — label block removed from template). */
  @Input() labelKey = 'Товар';
  /** Placeholder text shown inside the `p-autoComplete` input. */
  @Input() placeholder = 'Выбрать товар...';

  /** Pre-selection seed: a change here triggers a `getProductByCode` lookup to resolve the product model. */
  @Input() searchText: string = '';

  /**
   * When `true`, old/discontinued products may appear in search results.
   * Currently hard-disabled: `(this.allowOld && false)` always passes `false` to the API.
   * Do not change this expression without consulting `docs/api_parameter_alignment.md`.
   */
  @Input() allowOld: boolean = true;
  /**
   * When `true`, complex/bundle products are included in search results.
   * Passed through as `(this.allowComplex && true)` — the `&& true` is a no-op.
   */
  @Input() allowComplex: boolean = true;

  /** Passes through to `p-autoComplete [disabled]`. */
  @Input() disabled: boolean = false;
  /** Toggles label visibility (label block currently removed from template; binding kept for callers). */
  @Input() showLabel: boolean = true;

  _selectedProduct: Product;

  /** Emits the selected `Product` when the user picks a suggestion or confirms from the advanced-search dialog. */
  @Output()
  public selectedProduct: EventEmitter<Product> = new EventEmitter();

  private readonly destroy$ = new Subject<void>();
  private readonly _preSelectionCancel$ = new Subject<void>();

  private searchStr$ = new Subject<string>();

  displayPS: boolean = false;

  constructor(
    private prodApi: ProductApiService,
  ) { }

  /**
   * Forwards the `completeMethod` event query from `p-autoComplete` into the `searchStr$` pipeline.
   * Collapses embedded newlines/tabs (e.g. from pasting a whole row/table copied out of a
   * spreadsheet or email) into single spaces and caps the length before it ever reaches the
   * API — `getProductFastSearch` binds this string to a backend SQL `NVARCHAR(255)` parameter,
   * and forwarding a multi-line paste as-is could send several hundred characters, which the
   * backend used to turn into a 500 instead of an empty result set.
   */
  doSearch(event) {
    const sanitized: string = (event.query ?? '').replace(/[\r\n\t]+/g, ' ').trim().slice(0, 255);
    this.searchStr$.next(sanitized);
  }

  /**
   * Fires when the user clicks the chevron button on `p-autoComplete`.
   * Uses the current product's `PRODCODE` or falls back to `searchText` as the query,
   * so the dropdown button triggers a real search rather than pushing an empty string.
   */
  onDropdownClick(_event: unknown): void {
    console.log('*** ProductselectorComponent onDropdownClick 1: ' + JSON.stringify(_event));

    const currentQuery = (this.product as any)?.PRODCODE ?? this.searchText ?? '';
    console.log('*** ProductselectorComponent onDropdownClick 2: ' + currentQuery);

    if (currentQuery.length > 2) {
      this.searchStr$.next(currentQuery);
    }
  }

  /**
   * Responds to `@Input()` changes.
   * On `searchText` change: cancels any in-flight pre-selection request, issues a new
   * `getProductByCode` call, and seeds the live-search pipeline if the value is long enough.
   */
  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** ProductselectorComponent ngOnChanges');
    for (const propName of Object.keys(changes)) {
      const change = changes[propName];

      environment.production || console.log('*** ProductselectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));
      //console.log('*** ProductselectorComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'searchText':
          this._preSelectionCancel$.next();
          // NOTE: (this.allowOld && false) is intentionally forced to false — old products are excluded from search
          //       pending a decision to expose this as a runtime option (see docs/api_parameter_alignment.md).
          // NOTE: (this.allowComplex && true) passes allowComplex through — complex products are included/excluded per caller.
          this.prodApi.getProductByCode(this.searchText, (this.allowOld && false), (this.allowComplex && true)).pipe(
            takeUntil(this._preSelectionCancel$),
            takeUntil(this.destroy$)
          ).subscribe({
            next: data => { this.product = data[0] ?? null; },
            error: err => console.error('*** ProductselectorComponent getProductByCode error:', err)
          });
          if (((this.searchText || '').length > 2)) {
            this.searchStr$.next(this.searchText);
          }
          break;
      }
    }
  }

  /**
   * Cancels any in-flight pre-selection request via `_preSelectionCancel$`,
   * then completes `destroy$` to tear down all long-lived subscriptions.
   */
  ngOnDestroy(): void {
    // console.log('*** ProductselectorComponent ngOnDestroy: ');
    this._preSelectionCancel$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets up the `searchStr$` → debounced `getProductFastSearch` live-search pipeline.
   * The pipeline uses `catchError` inside `switchMap` so a single HTTP failure does not
   * terminate the stream. An initial search is triggered if `searchText` is already populated.
   */
  ngOnInit(): void {
    // console.log('*** ProductselectorComponent ngOnInit: ');

    this.searchStr$.pipe(
      debounceTime(150),
      filter(s => (s ?? '').length > 2),
      /*
        distinctUntilChanged is harmful here and should stay commented out — your current code is correct.

        Here's why it breaks the dropdown re-selection scenario:
        1.	User selects product "ABC" → searchStr$ emits "ABC".
        2.	User clicks the dropdown button → onDropdownClick pushes "ABC" again (from product.PRODCODE).
        3.	With distinctUntilChanged, the second "ABC" emission is swallowed — the search never fires.
        The distinctUntilChanged operator compares consecutive emissions on the Subject, not whether
        the HTTP request already ran. So re-opening the dropdown to browse the same prefix is silently blocked.
        debounceTime(200) already covers the main use-case distinctUntilChanged is usually
        added for (suppressing rapid duplicate keystrokes during fast typing).
        For a type-ahead + dropdown hybrid like this, debounceTime alone is sufficient.
      */
      // distinctUntilChanged(),
      tap(str => environment.production || console.log('*** ProductselectorComponent in switchMap: pgn=(' + str + ')')),
      switchMap(str =>
        // NOTE: same allowOld/allowComplex short-circuit expressions as getProductByCode above (see docs/api_parameter_alignment.md).
        this.prodApi.getProductFastSearch(str, (this.allowOld && false), (this.allowComplex && true)).pipe(
          map(data => { this.products = data; }),
          catchError(err => {
            console.error('*** ProductselectorComponent getProductFastSearch error:', err);
            return EMPTY;
          })
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe();

    if ((this.searchText ?? '').length > 2) {
      this.searchStr$.next(this.searchText);
    }
  }

  /**
   * Fires on PrimeNG `p-autoComplete`'s `(onSelect)` event — when the user picks a suggestion
   * from the dropdown list. Sets `product` to the selected value and emits `selectedProduct`.
   */
  onAutoCompleteSelect(event: {originalEvent: Event, value: Product}): void {
    environment.production || console.log('*** ProductselectorComponent onAutoCompleteSelect: ' + JSON.stringify(event.value));
    this.selectedProduct.emit(event.value);
  }

  /**
   * Fires when the user selects a product from the advanced-search (`app-products-search`) dialog.
   * Sets `product` to the chosen value and emits `selectedProduct`.
   */
  onProductSelection(event) {
    console.log('*** ProductselectorComponent onProductSelection: ' + JSON.stringify(event));

    this.product = event;

    this.selectedProduct.emit(this.product);
  }

}
